import { z } from "zod";
import { adminRoleSchema } from "./admin-rbac.js";

/**
 * Shared DTO cho tạo/mời admin mới + kích hoạt lại (US-AE-IMP.F2-0111 / @UC9137 · @UC9138).
 * Một nguồn sự thật cho api (S03 — endpoint create/reactivate) và admin web (S04 — dialog
 * "Thêm thành viên" + action "Kích hoạt lại"), tránh drift shape ở 2 nơi (DRY). Tái dùng
 * `AdminRole` từ admin-rbac (#8), KHÔNG định nghĩa lại role-list.
 *
 * BẢO MẬT (invariants):
 * - Request KHÔNG nhận password/secret/token — chỉ email + role. Backend sinh trạng thái
 *   `invited` + invite qua OutboxEvent (F3-0112 lo enroll/đặt mật khẩu).
 * - Response create/reactivate CHỈ mang định danh không nhạy cảm (id/email/role/status) —
 *   KHÔNG passwordHash/invite-secret/token (pii/AC5 no-deceive).
 */

/** Hằng status admin member 'invited' (đối xứng CHECK admin_members_status_check — S01). */
export const ADMIN_MEMBER_STATUS_INVITED = "invited" as const;
export const ADMIN_MEMBER_STATUS_ACTIVE = "active" as const;

/**
 * Body POST /v1/admin/members (create/invite). email normalize lowercase+trim rồi validate
 * format; role ∈ 4 vai (adminRoleSchema). `.strict()` chặn field lạ (vd password) tại biên.
 * role/email sai ⇒ ZodValidationPipe reject (→ 400/422) TRƯỚC khi vào service.
 */
export const createAdminMemberRequestSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    role: adminRoleSchema,
  })
  .strict();

export type CreateAdminMemberRequest = z.infer<typeof createAdminMemberRequestSchema>;

/**
 * Response 201 create: id/email/role/status (status = 'invited'). `.strict()` để test bảo đảm
 * KHÔNG lộ field secret (passwordHash/token). status là string (không literal cứng) để dung sai
 * tương lai, nhưng create luôn trả 'invited'.
 */
export const createAdminMemberResponseSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    role: adminRoleSchema,
    status: z.string(),
  })
  .strict();

export type CreateAdminMemberResponse = z.infer<typeof createAdminMemberResponseSchema>;

/**
 * Response 200 reactivate: adminMemberId + status='active' (mirror DeactivateAdminMemberResponse
 * shape). status literal 'active' — reactivate chỉ đưa disabled→active.
 */
export const reactivateAdminMemberResponseSchema = z
  .object({
    adminMemberId: z.string(),
    status: z.literal(ADMIN_MEMBER_STATUS_ACTIVE),
  })
  .strict();

export type ReactivateAdminMemberResponse = z.infer<
  typeof reactivateAdminMemberResponseSchema
>;

/**
 * Code lỗi 409 khi reactivate một member KHÔNG ở trạng thái 'disabled' (vd đã active) — dùng
 * chung api ↔ UI để hiển thị lỗi nhất quán ("thành viên đã hoạt động").
 */
export const ADMIN_MEMBER_NOT_DISABLED_CODE = "MEMBER_NOT_DISABLED" as const;
export const ADMIN_MEMBER_NOT_DISABLED_MESSAGE =
  "Chỉ có thể kích hoạt lại thành viên đang bị vô hiệu hoá.";

/**
 * Code lỗi 409 khi tạo admin với email đã tồn tại (trùng) — UI hiển thị "email đã được dùng".
 */
export const ADMIN_MEMBER_DUPLICATE_CODE = "MEMBER_DUPLICATE_EMAIL" as const;
export const ADMIN_MEMBER_DUPLICATE_MESSAGE = "Email này đã được sử dụng cho một thành viên khác.";

/**
 * Code lỗi 422 khi email create KHÔNG thuộc allowlist domain admin (fail-closed).
 */
export const ADMIN_MEMBER_DOMAIN_NOT_ALLOWED_CODE = "EMAIL_DOMAIN_NOT_ALLOWED" as const;
export const ADMIN_MEMBER_DOMAIN_NOT_ALLOWED_MESSAGE =
  "Email không thuộc miền được phép cho quản trị viên.";
